import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRecoilValue } from 'recoil';

import { API_URL } from 'constant';
import { useNetwork } from 'hooks';
import { DashboardsAtom } from 'views/dashboard-analytics';

/**
 * The counts shown against the sidebar's sub-routes.
 *
 * WHAT EACH NUMBER MEANS — they are deliberately NOT the same measure:
 *
 *   Onboarding Flow   +N   flows CREATED in the last 24 hours
 *   Sessions           N   sessions awaiting a human decision (not a 24h count:
 *                          a session that has been waiting three days is more
 *                          worth surfacing than one that arrived this morning)
 *   esign             +N   envelopes created in the last 24 hours
 *
 * The "+" prefix is applied by the renderer for the two that are arrival
 * counts, and omitted for the queue count, so a reader can tell "five new
 * things" from "five things waiting" without reading the label.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * LAST KNOWN COUNTS, PERSISTED.
 *
 * The badges kept disappearing, in two different ways:
 *
 *   - Expanding or collapsing a sidebar group REMOUNTS the tree that owns this
 *     hook. Every count began life as null/0 and only became real when its
 *     request returned, so each toggle blanked the numbers for a second or two.
 *   - A full page load threw away even that, so the sidebar rendered with no
 *     numbers at all until three requests had completed — one of which pulls
 *     3.1 MB.
 *
 * A number that flickers is worse than one that is slightly stale: the reader
 * cannot tell "nothing to report" from "not loaded yet", and a badge that comes
 * and goes reads as a bug even when the figure is right.
 *
 * So the counts live in localStorage and are painted immediately from the last
 * known values, then corrected when the fetches land. Overwritten ONLY by a
 * successful fetch — a failure leaves the previous figure standing rather than
 * replacing it with a zero we cannot support.
 *
 * Every access is wrapped: storage throws outright in some privacy modes, and a
 * sidebar that cannot render because a badge could not read a cache is a far
 * worse failure than a missing badge.
 */
const CACHE_KEY = 'simplici-sidebar-badges-v1';

interface IBadgeCache {
	flow: number;
	flowMore: boolean;
	sessions: number | null;
	sessionsPending: number | null;
	esign: number | null;
	esignPending: number | null;
	txnMonitoring: number | null;
	txnMonitoringPending: number | null;
}

const EMPTY: IBadgeCache = {
	flow: 0,
	flowMore: false,
	sessions: null,
	sessionsPending: null,
	esign: null,
	esignPending: null,
	txnMonitoring: null,
	txnMonitoringPending: null,
};

const readCache = (): IBadgeCache => {
	try {
		const raw = window.localStorage.getItem(CACHE_KEY);
		return raw ? { ...EMPTY, ...JSON.parse(raw) } : { ...EMPTY };
	} catch {
		return { ...EMPTY };
	}
};

const cache: IBadgeCache = readCache();

/* THROTTLED BY OUTCOME, NOT BY ATTEMPT.
 *
 * An earlier version set a "fetched" flag BEFORE the request. React's
 * StrictMode mounts, unmounts and remounts in development, which aborts that
 * first in-flight request — and the flag then blocked every retry, so the eSign
 * numbers never arrived at all. A guard that counts attempts rather than
 * successes turns one cancelled request into a permanently empty badge.
 *
 * So: remember when a fetch SUCCEEDED, and hold an in-flight promise so several
 * mounts share one request rather than each starting a 3.1 MB download. A
 * failure leaves the timestamp at 0, so the next mount tries again. */
const ESIGN_TTL = 5 * 60 * 1000;
let esignFetchedAt = 0;
let esignInFlight: Promise<void> | null = null;

/* ...BUT "THE NEXT MOUNT" NEVER COMES.
 *
 * The note above is right that a failure leaves the timestamp at 0 so the next
 * mount retries. The sidebar is mounted for the whole session, so there is no
 * next mount: the effect below runs once, and the five-minute interval
 * deliberately refreshes only flows and sessions. One failed call therefore
 * meant NO eSign badges until the tab was reloaded.
 *
 * Login is precisely when that call is most likely to fail — the sidebar mounts
 * while auth is still settling, so the request can go out before the token is in
 * place and come back 401. The badges were then missing for the whole session,
 * which is what "no numbers for esign on log in" was.
 *
 * So retry until there ARE figures, then stop. This is not a refresh loop: once
 * both counts are non-null the tick is a comparison and nothing else, so the
 * 3.1 MB request still happens once per load in the normal case.
 *
 * Bounded, because the loader treats an empty payload as a failure and never
 * writes a 0 — without a cap, a business that genuinely has no envelopes would
 * retry forever. Ten attempts at 15s covers an unusually slow login and then
 * gives up rather than polling all day. */
const ESIGN_RETRY_MS = 15 * 1000;
const ESIGN_MAX_RETRIES = 10;
let esignRetries = 0;

/* THE CACHE IS SHARED; THE REACT STATE WAS NOT.
 *
 * Every loader writes into the module-level `cache` and then calls its OWN
 * instance's setters. That is fine with one instance and silently broken with
 * two, which is exactly what StrictMode gives you in development: instance A
 * mounts, starts the fetches, unmounts; instance B mounts and skips them,
 * because A's success has already stamped the throttle. A's setters are dead by
 * then, and B's `useState(cache.esign)` initialiser ran while the value was
 * still null -- so `cache` holds the right numbers and the sidebar renders
 * none of them.
 *
 * That is the bug behind "no numbers for esign on log in". Measured: the cache
 * held sessions 141 / pending 239 / esign 282 / esignPending 697 eight seconds
 * in, while the sidebar showed nothing for another thirty -- until an unrelated
 * request re-rendered it and a fresh instance re-read the cache on the way up.
 *
 * So instances SUBSCRIBE. A loader writes the cache once and notifies whoever
 * is mounted, and a new instance adopts what a previous one already fetched
 * instead of waiting for a fetch it is never going to make. */
type BadgeListener = () => void;
const listeners = new Set<BadgeListener>();
const notify = () => {
	listeners.forEach(l => {
		try {
			l();
		} catch {
			/* one bad subscriber must not stop the others */
		}
	});
};

const persist = () => {
	try {
		window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
	} catch {
		/* storage unavailable — the in-memory copy still serves this tab */
	}
};

export interface ISidebarBadges {
	/** flows created in the last 24h */
	onboardingFlow: number;
	/** true when there were MORE than one page of them — the badge reads "20+" */
	onboardingFlowMore: boolean;
	/** sessions created in the last 24h */
	sessions: number | null;
	/** sessions waiting on a human — a QUEUE, not a 24h figure */
	sessionsPending: number;
	/** envelopes created in the last 24h, or null when it could not be counted */
	esign: number | null;
	/** envelopes still awaiting completion — a queue, not a 24h figure */
	esignPending: number | null;
	/** TM alerts raised in the last 24h, or null when it could not be counted */
	txnMonitoring: number | null;
	/** TM alerts still awaiting a person — a queue, not a 24h figure */
	txnMonitoringPending: number | null;
}

export const useSidebarBadges = (): ISidebarBadges => {
	const dashboards = useRecoilValue(DashboardsAtom);
	const { get: getFlows } = useNetwork({ updateState: false });
	const [flowCount, setFlowCount] = useState(cache.flow);
	const [flowMore, setFlowMore] = useState(cache.flowMore);

	/**
	 * Sessions awaiting review, read off the summary the shell already fetched
	 * into DashboardsAtom at mount. No extra request for this one.
	 */
	/**
	 * Sessions awaiting review, off the summary the shell fetches at mount.
	 *
	 * CACHED LIKE THE REST. This read DashboardsAtom directly and rendered
	 * whatever was in it — which is nothing until /onboarding-summary returns, so
	 * on every page load the amber count was simply absent for the first second
	 * or two, and absent entirely on any route that mounts the sidebar before the
	 * summary is primed. It was the one number here not backed by the cache, and
	 * it was the one that kept vanishing after the others were fixed.
	 *
	 * A zero from an empty atom is NOT written through: an unloaded summary and a
	 * genuinely empty queue look identical at that point, and overwriting a real
	 * figure with a placeholder zero is the failure this cache exists to prevent.
	 */
	const sessionsPending = useMemo(() => {
		const raw = Number(
			(dashboards?.data?.summary?.session ?? []).find(
				(s: any) => s.id === 'processing'
			)?.value ?? NaN
		);
		if (Number.isFinite(raw)) {
			cache.sessionsPending = raw;
			persist();
			notify();
			return raw;
		}
		return cache.sessionsPending ?? 0;
	}, [dashboards]);

	/**
	 * Flows created in the last 24 hours.
	 *
	 * `/complex-flow` accepts the date window server-side and answers with just
	 * that page, so this costs one small request rather than pulling the whole
	 * list back to count it client-side.
	 *
	 * LIMIT IS CAPPED AT 20, SERVER-SIDE. This first asked for `limit=100` and
	 * every call answered
	 *   "Record limit should always be a positive integer, and should not
	 *    exceed 20"
	 * — a 400 on page load and again on every five-minute refresh, for a badge
	 * that then silently showed 0. The cap is the endpoint's, so ask for what it
	 * allows and read `totalPages` to know whether there is more beyond the
	 * page: a second page means "more than 20 today", which the badge reports as
	 * 20 with a "20+" tooltip rather than pretending to an exact figure it did
	 * not fetch.
	 */
	const PAGE_LIMIT = 20;

	const loadFlows = useCallback(async () => {
		const to = new Date();
		const from = new Date(to.getTime() - DAY_MS);
		try {
			const resp = await getFlows(
				`${API_URL.COMPLEX_FLOW}?isDeleted=false&page=1&limit=${PAGE_LIMIT}` +
					`&startDate=${from.toISOString()}&endDate=${to.toISOString()}` +
					`&dateFilterBy=createdAt`
			);
			// the endpoint answers `data: [{ totalPages, result: [...] }]`
			const page = (resp?.data ?? resp)?.[0] ?? {};
			/* Unlike /envelopes, an empty page here IS meaningful — "no flows were
			   created today" is a normal answer and must be allowed to overwrite a
			   previous count. But a MISSING `result` array means the request did
			   not answer properly, and that must not be counted as zero. */
			if (!Array.isArray(page?.result)) return;
			const rows = page.result;
			const pages = Number(page?.totalPages ?? 1) || 1;
			cache.flow = rows.length;
			cache.flowMore = pages > 1;
			persist();
			notify();
			setFlowCount(cache.flow);
			setFlowMore(cache.flowMore);
		} catch {
			/* Keep the last good figure. Zeroing here is what made a failed
			   refresh look like "nothing happened today". */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	/**
	 * Sessions created in the last 24 hours.
	 *
	 * `/complex-sessions` takes the same date window as `/complex-flow` and,
	 * with `pagination=true`, answers a `total` alongside the page — so asking
	 * for pageSize=1 buys the COUNT for the price of one row rather than pulling
	 * 1,173 sessions back to length-check them. Verified: unfiltered total 1173,
	 * same call windowed to 24h returns 102.
	 */
	const [sessionCount, setSessionCount] = useState<number | null>(cache.sessions);

	const loadSessions = useCallback(async () => {
		const to = new Date();
		const from = new Date(to.getTime() - DAY_MS);
		try {
			const resp = await getFlows(
				`${API_URL.COMPLEX_SESSION}?pagination=true&pageNo=1&pageSize=1` +
					`&startDate=${from.toISOString()}&endDate=${to.toISOString()}` +
					`&dateFilterBy=createdAt`
			);
			const page = (resp?.data ?? resp)?.[0] ?? {};
			const total = Number(page?.total);
			if (Number.isFinite(total)) {
				cache.sessions = total;
				persist();
			notify();
				setSessionCount(total);
			}
		} catch {
			/* keep the last good figure */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Transaction-monitoring alerts: raised in the last 24h, and the queue still
	 * waiting on a person.
	 *
	 * BOTH NUMBERS COME FROM ONE CALL AT pageSize=1, because `/alerts` answers a
	 * `counts` block beside the page — open / investigating / awaiting / sar /
	 * done / all / mine, and now `recent`. So this costs one row, the way
	 * /complex-sessions does, and NOT the way eSign has to work: that endpoint
	 * offers no total and no date window, so it pulls the whole book back to
	 * length-check it.
	 *
	 * `recent` is counted SERVER-SIDE on purpose. This endpoint caps a page at
	 * 40 rows and an alert is a fat document (~250KB per 40), so counting a 24h
	 * window in the browser would mean paging the entire queue for one number,
	 * and the cost would grow with the book.
	 *
	 * PENDING IS COUNTED BY EXCLUSION, same reasoning as eSign: `done` is the
	 * only terminal bucket, so anything else is still someone's problem. An
	 * alert state added later then counts as pending by default rather than
	 * being silently dropped by an allow-list that never heard of it. `sar` is
	 * deliberately INSIDE the queue — a report that needs filing is not finished
	 * work.
	 */
	const [txnCount, setTxnCount] = useState<number | null>(cache.txnMonitoring);
	const [txnPending, setTxnPending] = useState<number | null>(
		cache.txnMonitoringPending
	);

	const loadTxnMonitoring = useCallback(async () => {
		try {
			const resp = await getFlows(`${API_URL.TM_ALERTS}?pageNo=1&pageSize=1`);
			const counts = (resp?.data ?? resp)?.counts;
			/* A MISSING counts BLOCK IS A FAILURE, NOT A ZERO — the same trap the
			   eSign loader documents. A zero renders no badge, and persisting it
			   would replace good figures with nothing on every retry. */
			if (!counts || typeof counts !== 'object') return;
			/* THE BLUE PILL COUNTS TRANSACTIONS, NOT ALERTS.
			   `counts.recent` beside it is alerts-in-24h and is deliberately NOT
			   used here: on every other row the "+N" means "new work arrived",
			   counting the primary object — eSign counts envelopes, not the
			   subset needing attention. Counting alerts made a day that ingested
			   fifty thousand clean transactions and raised nothing show no pill
			   at all, so the busiest possible day looked identical to an idle
			   one. `recent` is left in the payload because the queue screens use
			   it. */
			const recent = Number(counts.transactionsRecent);
			const all = Number(counts.all);
			const done = Number(counts.done);
			if (Number.isFinite(recent)) {
				cache.txnMonitoring = recent;
				setTxnCount(recent);
			}
			if (Number.isFinite(all) && Number.isFinite(done)) {
				cache.txnMonitoringPending = Math.max(all - done, 0);
				setTxnPending(cache.txnMonitoringPending);
			}
			persist();
			notify();
		} catch {
			/* keep the last good figure */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/**
	 * Envelopes created in the last 24 hours.
	 *
	 * THIS ONE IS EXPENSIVE AND THERE IS NO CHEAPER WAY TODAY. `/envelopes`
	 * has no count mode: `limit`/`page`, `pagination=true` and a date window all
	 * make it answer `data: null`, so the only call that returns anything is the
	 * unfiltered list — 898 envelopes, 3.1 MB. The count is therefore taken
	 * client-side from `createdAt`.
	 *
	 * Fetched ONCE per app load and deliberately NOT on the five-minute refresh
	 * the other two use: a 3.1 MB request every five minutes to keep a badge
	 * current is not a trade worth making. A `total` on this endpoint — the same
	 * thing /complex-sessions already returns — would make this as cheap as the
	 * other two and let it refresh with them.
	 */
	const [esignCount, setEsignCount] = useState<number | null>(cache.esign);
	const [esignPending, setEsignPending] = useState<number | null>(cache.esignPending);

	const loadEsign = useCallback(async () => {
		const since = Date.now() - DAY_MS;
		try {
			const resp = await getFlows(
				`${API_URL.ENVELOPE}?status=configured&status=completed` +
					`&status=voided&status=underReview`
			);
			/* AN EMPTY PAYLOAD IS A FAILURE HERE, NOT A ZERO.
			 *
			 * This coerced a non-array `data` to [] and counted it, which meant a
			 * response of `data: null` wrote ZERO into both counts — and a zero
			 * renders no badge at all. Persisted, that zero then replaced the good
			 * figures, so the eSign numbers disappeared on refresh and never came
			 * back: every retry re-wrote the same zero.
			 *
			 * `/envelopes` answers `data: null` for any query shape it does not
			 * support (limit, page, pagination, a date window — all of them), so an
			 * empty result from this endpoint is far more likely a rejected call
			 * than a business with no envelopes. Treated as a failure: keep the
			 * last known figures and leave the timestamp unstamped so the next
			 * mount tries again. A business that genuinely has none shows no badge
			 * either way, so the guard costs nothing.
			 */
			const rows = Array.isArray(resp?.data) ? resp.data : null;
			if (!rows || !rows.length) return;
			cache.esign = rows.filter((e: any) => {
				const t = new Date(e?.createdAt ?? 0).getTime();
				return Number.isFinite(t) && t >= since;
			}).length;
			setEsignCount(cache.esign);
			/* PENDING COMPLETION = everything that is not finished, counted by
			   EXCLUSION rather than by listing the statuses that mean "waiting".
			   `completed` and `voided` are the only two terminal states; a new
			   in-flight status added later should count as pending by default,
			   whereas an allow-list would silently drop it and under-report the
			   queue. Live distribution on this business: 696 configured, 202
			   completed. */
			cache.esignPending = rows.filter(
				(e: any) => !['completed', 'voided'].includes(String(e?.status ?? ''))
			).length;
			persist();
			notify();
			setEsignPending(cache.esignPending);
			/* Stamped only on success — a failure must leave this at 0 so the next
			   mount retries instead of inheriting a silent permanent gap. */
			esignFetchedAt = Date.now();
		} catch {
			/* keep the last good figures */
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	/* THE EFFECT GOES LAST, after every loader it calls.
	   It sat above loadSessions/loadEsign, which are `const` — the callback only
	   runs after render so it worked, but a reader had to prove that to
	   themselves, and moving one line would have turned it into a temporal dead
	   zone with no warning. Declared before use instead. */
	useEffect(() => {
		/* SUBSCRIBE FIRST, AND ADOPT WHAT IS ALREADY THERE.
		   The immediate call is the important half: this instance may be the
		   remount that skips every fetch because a previous one already
		   succeeded, and without it the numbers sit in `cache` unread. */
		const adopt = () => {
			setFlowCount(cache.flow);
			setFlowMore(cache.flowMore);
			setSessionCount(cache.sessions);
			setEsignCount(cache.esign);
			setEsignPending(cache.esignPending);
			setTxnCount(cache.txnMonitoring);
			setTxnPending(cache.txnMonitoringPending);
		};
		listeners.add(adopt);
		adopt();

		loadFlows();
		loadSessions();
		/* Refreshed on the same five-minute tick as flows and sessions, NOT held
		   back like eSign. It is a one-row request — the alert counts come off
		   the server's own aggregation — so there is no reason to make an
		   operator reload the panel to see that an alert arrived. */
		loadTxnMonitoring();
		// esign is fetched once per TAB, not per mount: it is a 3.1 MB request and
		// remounting on every sidebar toggle would repeat it.
		if (!esignInFlight && Date.now() - esignFetchedAt > ESIGN_TTL) {
			esignInFlight = loadEsign().finally(() => {
				esignInFlight = null;
			});
		}
		// Refreshed rather than fetched once: the sidebar is mounted for the whole
		// session, and a count that was true at login is worse than no count.
		const t = setInterval(() => {
			loadFlows();
			loadSessions();
			loadTxnMonitoring();
		}, 5 * 60 * 1000);

		/* Keep trying until eSign has figures — see ESIGN_RETRY_MS above. Once
		   both counts are set this tick does nothing, so it costs a comparison
		   every 15s for the rest of the session and issues no request. */
		const esignRetry = setInterval(() => {
			if (cache.esign !== null && cache.esignPending !== null) return;
			if (esignInFlight || esignRetries >= ESIGN_MAX_RETRIES) return;
			esignRetries += 1;
			esignInFlight = loadEsign().finally(() => {
				esignInFlight = null;
			});
		}, ESIGN_RETRY_MS);

		return () => {
			listeners.delete(adopt);
			clearInterval(t);
			clearInterval(esignRetry);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return {
		onboardingFlow: flowCount,
		onboardingFlowMore: flowMore,
		sessions: sessionCount,
		sessionsPending,
		esign: esignCount,
		esignPending,
		txnMonitoring: txnCount,
		txnMonitoringPending: txnPending,
	};
};
